import { useRef, useEffect } from "react";
import { convertEmailsToMailToLinks } from "./email-utils.js";

export const getId = () => {
  return (
    Date.now().toString(36) +
    Math.floor(
      Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)
    ).toString(36)
  );
};

export const isUndefined = (foo) => {
  return foo == undefined;
};

export const isEmpty = (foo) => {
  return isUndefined(foo) || foo.length == 0;
};

export const isNotEmpty = (foo) => {
  return !isEmpty(foo);
}

export const isDefined = (foo) => {
  return !isUndefined(foo);
};

export const platformsThatSupportAnalytics = [
  "FACEBOOK",
  "INSTAGRAM",
  "LINKEDIN",
  "GMB",
  "THREADS"
];

let platformsThatSupportAccountAnalytics = [...platformsThatSupportAnalytics, "PINTEREST", "TIKTOK", "YOUTUBE"]

export const appsThatSupportAnalytics = ["FACEBOOK_ADS"];

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const containsWebsiteAnalyticsIntegration = (integrations) => {
  if (isEmpty(integrations)) {
    return false;
  }

  for (var i = 0; i < integrations.length; i++) {
    var integration = integrations[i];
    if (
      integration.appType == "GOOGLE_ANALYTICS" ||
      integration.appType == "GOOGLE_ANALYTICS4"
    ) {
      return true;
    }
  }

  return false;
};

export const containsAdAnalyticsIntegration = (integrations) => {
  if (isEmpty(integrations)) {
    return false;
  }

  for (let i = 0; i < integrations.length; i++) {
    const integration = integrations[i];
    if (supportsAdAnalytics(integration.appType)) {
      return true;
    }
  }
  return false;
};

export const getWebsiteAnalyticsFirstIntegration = (integrations) => {
  if (isEmpty(integrations)) {
    return {};
  }

  for (var i = 0; i < integrations.length; i++) {
    var integration = integrations[i];
    console.log("app type is " + integration.appType);
    if (integration.appType === "GOOGLE_ANALYTICS4") {
      return integration;
    }
  }

  return {};
};

export const supportsAdAnalytics = (appType) => {
  return appsThatSupportAnalytics.indexOf(appType) >= 0;
};

export const supportsAnalytics = (platformType) => {
  return platformsThatSupportAnalytics.indexOf(platformType) >= 0;
};

export const supportsAccountAnalytics = (platformType) => {
  return platformsThatSupportAccountAnalytics.indexOf(platformType) >= 0;
};

export const containsOrganicAnalyticsAccounts = (accountList) => {
  if (isEmpty(accountList)) {
    return false;
  }

  for (let i = 0; i < accountList.length; i++) {
    const account = accountList[i];
    if (supportsAnalytics(account.platformType) || supportsAccountAnalytics(account.platformType)) {
      return true;
    }
  }
  return false;
};


let monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

function addZero(i) {
  if (i < 10) {
    i = "0" + i;
  }
  return i;
}

function prettyTime(date) {
  const meridian = date.getHours() >= 12 ? "pm" : "am";
  let adjustedHour = date.getHours() % 12;
  adjustedHour = adjustedHour === 0 ? 12 : adjustedHour;
  return adjustedHour + ":" + addZero(date.getMinutes()) + meridian;
}

function prettyDateWithTime(date) {
  return (
      monthNames[date.getMonth()] +
      " " +
      date.getDate() +
      ", " +
      date.getFullYear() +
      ", " +
      prettyTime(date)
  );
}

export const formatNum = function (numFollowers, allowNegatives) {
  var numFollowersFormatted;

  if (numFollowers < 0 && !allowNegatives) {
    numFollowersFormatted = "n/a";
  } else if (numFollowers / 1000000 >= 1) {
    const millionFollowers = numFollowers / 1000000;
    numFollowersFormatted =
        parseFloat(millionFollowers).toFixed(1) + "M";
  } else if (numFollowers / 1000 >= 1) {
    const thousandFollowers = numFollowers / 1000;
    numFollowersFormatted =
        parseFloat(thousandFollowers).toFixed(1) + "k";
  } else {
    numFollowersFormatted = numFollowers;
  }

  return numFollowersFormatted;
};

export const enrichPost = function (post) {
  const date = new Date(post.createdAtMillis);
  post.date = prettyDateWithTime(date);
  post.numImpressionsFormatted = formatNum(post.totalReach);
  post.numLikesFormatted = formatNum(post.likeCount);
  post.numClicksFormatted = formatNum(post.clickCount);
  post.numSharesFormatted = formatNum(post.shareCount);
  post.numCommentsFormatted = formatNum(post.commentCount);

  post.hasVideo = isNotEmpty(post.videoUrl);
  post.hasLink = isNotEmpty(post.link);
  return post;
};

export const parsePostText = (post) => {
  let basePlatformUrl;
  if (post === undefined || isEmpty(post.text)) {
    return "";
  }

  const urls =
      /(\b(https?|ftp):\/\/[A-Z0-9+&@#\/%?=~_|!:,.;-]*[-A-Z0-9+&@#\/%=~_|])/gim;
  const hashtags = /(^|\s)#([\p{L}\d_]+)/gimu;
  const userHandles = /(^|\s)@([a-z\d-_]+)/gim;

  let text = post.text;
  const platform = post.platformType;

  if (text.match(urls)) {
    text = text.replace(
        urls,
        '<a style="text-decoration: none;" href="$1" target="_blank">$1</a>'
    );
  }

  // Parse emails using the centralized email utility
  text = convertEmailsToMailToLinks(text);

  try {
    if (text.match(hashtags)) {
      basePlatformUrl = "https://twitter.com/hashtag/";

      switch (platform) {
        case "FACEBOOK":
          basePlatformUrl =
              "https://www.facebook.com/hashtag/";
          break;

        case "INSTAGRAM":
          basePlatformUrl =
              "https://www.instagram.com/explore/tags/";
          break;

        case "TWITTER":
          basePlatformUrl = "https://twitter.com/hashtag/";
          break;

        case "LINKEDIN":
          basePlatformUrl =
              "https://www.linkedin.com/feed/hashtag/?keywords=";
          break;

        case "YOUTUBE":
          basePlatformUrl =
              "https://www.youtube.com/results?search_query=%23";
          break;
      }

      text = text.replace(hashtags, function(match, prefix, tag) {
        return '<a style="text-decoration: none;" href="' + basePlatformUrl + encodeURIComponent(tag) + '" target="_blank">' + prefix + '#' + tag + '</a>';
      });
    }
  } catch (error) {
    console.error("Failed to parse hashtags", error);
  }

  if (
      text.match(userHandles) &&
      (platform === "INSTAGRAM" || platform === "TWITTER")
  ) {
    basePlatformUrl = "https://twitter.com/";

    switch (platform) {
      case "INSTAGRAM":
        basePlatformUrl = "https://www.instagram.com/";
        break;

      case "TWITTER":
        basePlatformUrl = "https://twitter.com/";
        break;
    }

    text = text.replace(
        userHandles,
        '<a style="text-decoration: none;" href="' +
        basePlatformUrl +
        '$2" target="_blank">$1@$2</a>'
    );
  }

  return text;
}

